import * as Phaser from 'phaser';
import { EventBus } from '../core/EventBus';
import { EvolutionRewardPayload, UpgradeOption } from '../systems/UpgradeSystem';
import { LocalizationManager } from '../managers/LocalizationManager';
import { UITheme } from './UITheme';
import { UIButton, bindUIFocus } from './UIButton';
import { createLogger } from '../utils/Logger';

export class ChestOpenScreen extends Phaser.GameObjects.Container {
    private eventBus = EventBus.getInstance();
    private background: Phaser.GameObjects.Rectangle;
    private titleText: Phaser.GameObjects.Text;
    private contentText: Phaser.GameObjects.Text;
    private button: UIButton;
    private cardsContainer: Phaser.GameObjects.Container;
    private currentEvolution: EvolutionRewardPayload | null = null;
    private currentUpgrades: UpgradeOption[] | null = null;
    private releaseFocus?: () => void;
    private reducedFlashes = false;
    private readonly logger = createLogger('ChestOpenScreen');

    public setReducedFlashes(value: boolean): void {
        this.reducedFlashes = value;
        if (value) { this.scene.tweens.killTweensOf(this.cardsContainer); this.cardsContainer.setAlpha(1); }
    }

    constructor(scene: Phaser.Scene) {
        super(scene,0,0);
        const loc=LocalizationManager.getInstance();
        this.setDepth(2000).setScrollFactor(0).setVisible(false);
        this.background=scene.add.rectangle(0,0,scene.scale.width,scene.scale.height,UITheme.background,0.97).setOrigin(0).setInteractive();
        this.background.on('pointerdown',(_p:unknown,_x:number,_y:number,e?:Phaser.Types.Input.EventData)=>e?.stopPropagation());
        this.titleText=scene.add.text(0,0,loc.getText('chest.title'),{fontFamily:UITheme.heading,fontSize:'28px',color:'#ecc778',align:'center'}).setOrigin(0.5);
        this.contentText=scene.add.text(0,0,'',{fontFamily:UITheme.body,fontSize:'18px',color:UITheme.text,align:'center'}).setOrigin(0.5);
        this.cardsContainer=scene.add.container(0,0);
        this.button=new UIButton(scene,0,0,loc.getText('chest.collect'),()=>this.onClose());
        this.add([this.background,this.titleText,this.contentText,this.cardsContainer,this.button]);
        scene.add.existing(this);
    }
    public show(reward?:string):void {
        if(this.visible)return;
        this.currentEvolution=null;this.currentUpgrades=null;
        this.contentText.setText(reward ?? LocalizationManager.getInstance().getText('chest.empty'));
        this.open();
    }
    public showUpgrades(upgrades:UpgradeOption[]):void {
        if(this.visible)return;
        this.currentEvolution=null;this.currentUpgrades=[...upgrades];
        this.contentText.setText('');this.open();
    }
    public showEvolution(evolution:EvolutionRewardPayload):void {
        if(this.visible)return;
        this.currentEvolution=evolution;this.currentUpgrades=null;
        this.contentText.setText('');this.open();
    }
    private open():void {
        if (this.visible) return;
        const loc=LocalizationManager.getInstance();
        this.titleText.setText(loc.getText(this.currentEvolution?'chest.evolution':'chest.title'));
        this.setVisible(true);this.resize();
        this.scene.tweens.killTweensOf(this.cardsContainer);
        this.cardsContainer.setAlpha(this.reducedFlashes ? 1 : 0.55);
        if (!this.reducedFlashes) this.scene.tweens.add({ targets: this.cardsContainer, alpha: 1, duration: this.currentEvolution ? 420 : 220 });
        this.logger.debug('reveal:started', { mode: this.currentEvolution ? 'evolution' : 'upgrades', reducedFlashes: this.reducedFlashes });
        this.releaseFocus?.();this.releaseFocus=bindUIFocus(this.scene,[this.button]);
    }
    public resize():void {
        const {width,height}=this.scene.scale;
        this.background.setSize(width,height);
        this.titleText.setPosition(width/2,32).setWordWrapWidth(width-32);
        this.contentText.setPosition(width/2,height/2).setWordWrapWidth(width-48);
        this.button.setPosition(width/2,height-30);
        this.cardsContainer.setPosition(0,0);
        this.cardsContainer.removeAll(true);
        const loc=LocalizationManager.getInstance();
        const rewards=this.currentEvolution ? [{title:this.currentEvolution.name,description:this.currentEvolution.descriptionKey ? loc.getText(this.currentEvolution.descriptionKey) : loc.getText('chest.evolved_into',[this.currentEvolution.name]),icon:this.currentEvolution.icon,type:'weapon'}] : this.currentUpgrades ?? [];
        const vertical=width<height;
        const count=rewards.length;
        const cardWidth=vertical?Math.min(520,width-40):Math.min(420,(width-40)/Math.max(1,count)-12);
        const cardHeight=vertical?Math.min(220,(height-140)/Math.max(1,count)-12):Math.min(240,height-140);
        rewards.forEach((reward,i)=>{
            const x=vertical?width/2:width/2+(i-(count-1)/2)*(cardWidth+12);
            const y=vertical?90+cardHeight/2+i*(cardHeight+12):(height+12)/2;
            const card=this.scene.add.container(x,y);
            const bg=this.scene.add.rectangle(0,0,cardWidth,cardHeight,UITheme.panel).setStrokeStyle(this.currentEvolution ? 3 : 2,this.currentEvolution ? 0xecc778 : 0x748d72);
            card.add(bg);
            if(reward.icon && this.scene.textures.exists(reward.icon)) card.add(this.scene.add.sprite(-cardWidth/2+28,-cardHeight/2+30,reward.icon).setDisplaySize(36,36));
            const title=this.scene.add.text(-cardWidth/2+54,-cardHeight/2+14,reward.title,{fontFamily:UITheme.body,fontSize:'18px',color:UITheme.text,fontStyle:'bold',wordWrap:{width:cardWidth-66}});
            const categoryY=Math.max(-cardHeight/2+60,title.y+title.height+6);
            const category=this.scene.add.text(-cardWidth/2+14,categoryY,loc.getText(reward.type==='consumable'?'ui.consumables':reward.type?.startsWith('passive')?'ui.passives':'ui.weapons'),{fontFamily:UITheme.body,fontSize:'16px',color:'#ecc778'});
            const desc=this.scene.add.text(-cardWidth/2+14,categoryY+24,reward.description,{fontFamily:UITheme.body,fontSize:'16px',color:UITheme.muted,wordWrap:{width:cardWidth-28}});
            card.add([title,category,desc]);this.cardsContainer.add(card);
        });
    }
    private onClose():void {
        if(!this.visible)return;
        const data={evolution:this.currentEvolution,upgrades:this.currentUpgrades};
        this.scene.tweens.killTweensOf(this.cardsContainer);
        this.logger.debug('reveal:closed', { mode: this.currentEvolution ? 'evolution' : 'upgrades' });
        this.setVisible(false);this.releaseFocus?.();this.releaseFocus=undefined;
        this.currentEvolution=null;this.currentUpgrades=null;this.cardsContainer.removeAll(true);
        this.eventBus.publish({type:'chest:closed',data});
    }
}
